import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext.js';

// A simple debounce hook
const useDebounce = (value, delay) => {
    const [debouncedValue, setDebouncedValue] = useState(value);
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);
        return () => clearTimeout(handler);
    }, [value, delay]);
    return debouncedValue;
};

function ManageMembers() {
    const { token, user: adminUser } = useAuth();
    const navigate = useNavigate();
    const [users, setUsers] = useState([]);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const debouncedSearchTerm = useDebounce(searchTerm, 500);

    const fetchUsers = useCallback(async (currentPage, search) => {
        try {
            const params = new URLSearchParams({ page: currentPage, search: search });
            const response = await fetch(`https://members.abssvss.com/members_api/get_all_users_admin.php?${params.toString()}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.message);
            setUsers(data.users);
            setTotalPages(data.totalPages);
        } catch (error) {
            console.error(error.message);
        }
    }, [token]);

    useEffect(() => {
        setPage(1);
        fetchUsers(1, debouncedSearchTerm);
    }, [debouncedSearchTerm, fetchUsers]);

    useEffect(() => {
        if(page > 1) {
            fetchUsers(page, debouncedSearchTerm);
        }
    }, [page, fetchUsers]);

    const handlePageChange = (newPage) => {
        if (newPage > 0 && newPage <= totalPages) {
            setPage(newPage);
        }
    };
    
    const handleDelete = async (userId) => {
        if (!window.confirm('Are you sure you want to delete this member? This action cannot be undone.')) {
            return;
        }
        // Delete logic would go here
        alert(`Note: Delete functionality for user ID ${userId} needs to be connected to a backend script.`);
    };

    return (
        <div className="space-y-6">
            <h2 className="text-2xl font-semibold text-gray-700">Manage All Members</h2>
            <input
                type="text"
                placeholder="Search by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full p-2 border rounded-md"
            />
            <div className="overflow-x-auto">
                <table className="min-w-full bg-white border">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase">Image</th>
                            <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                            <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                            <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                            <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                            <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y">
                        {users.map(user => (
                            <tr key={user.id}>
                                <td className="py-2 px-4">
                                    <img
                                        src={user.profilePictureUrl ? `https://members.abssvss.com${user.profilePictureUrl}` : `https://ui-avatars.com/api/?name=${user.firstName}+${user.lastName}&background=8b0000&color=fff&size=40`}
                                        alt="Profile"
                                        className="w-10 h-10 rounded-full object-cover"
                                    />
                                </td>
                                <td className="py-2 px-4 whitespace-nowrap">{user.firstName} {user.lastName}</td>
                                <td className="py-2 px-4">{user.email}</td>
                                <td className="py-2 px-4">{user.role}</td>
                                <td className="py-2 px-4">
                                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${user.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                                        {user.status}
                                    </span>
                                </td>
                                <td className="py-2 px-4 whitespace-nowrap">
                                    <button onClick={() => navigate(`/portal-management-7a8b9c/edit-user/${user.id}`)} className="bg-blue-500 text-white font-bold py-1 px-3 rounded mr-2">Edit</button>
                                    {adminUser?.role === 'superadmin' && (
                                        <button onClick={() => handleDelete(user.id)} className="bg-red-500 text-white font-bold py-1 px-3 rounded">Delete</button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            {/* Pagination */}
            <div className="mt-4 flex justify-between items-center">
                <button onClick={() => handlePageChange(page - 1)} disabled={page <= 1} className="px-4 py-2 bg-gray-300 rounded disabled:opacity-50">Previous</button>
                <span>Page {page} of {totalPages}</span>
                <button onClick={() => handlePageChange(page + 1)} disabled={page >= totalPages} className="px-4 py-2 bg-gray-300 rounded disabled:opacity-50">Next</button>
            </div>
        </div>
    );
}

export default ManageMembers;

